/**
 * The router: an `InputEvent` for a binding → shaping → the target's writer.
 *
 * Backend-agnostic and store-free: targets are reached through `resolvePort(parsedTarget)`, which returns
 *   `{ schema, read(), begin(), write(v), end(), set(v), takeover?, idleMs? }` for a continuous target, or `{ fire() }` for an action — null when the target cannot be written now (nothing selected, a driven param on a non-runner).
 *
 * ⚠️ A CONTROLLER MOVE IS A GESTURE, as a drag is: `begin` (one undo step), transient `write`s, then `end` once the control has been idle for `idleMs` — the idle stands in for pointer-up. Writes coalesce: the latest value per target is applied once per `schedule` tick (a frame), never per MIDI message, and nothing on this path touches React.
 *
 * During a gesture the router's own last write is the param's current value: an object param's frames are held on Pixi, so the store would still read the pre-gesture value and look like something else moved it.
 *
 * Mode × event, for a continuous target:
 *   absolute   abs → position (through takeover) · press → position 1
 *   relative   rel → steps from where the param is
 *   velocity   press → velocity, no takeover · abs → position, no takeover
 *   toggle     press → the other end
 *   momentary  press → max · release → min
 *   trigger    press → max
 * An action target fires on press, or on any nonzero relative step. In the button modes and on actions a CC is a button: crossing the midpoint up is a press, down a release.
 */
import { BUTTON_MODES, RANGE_SIZE, parseTarget, rangeEntry, sourceKey } from './model'
import { clamp01, fromDomain, relativeTick, toDomain } from './shape'
import { createTakeover, noteWritten, takeover } from './takeover'

// An idle this long ends a gesture — pointer-up for a hand that never lets go.
export const IDLE_MS = 200

// A control heard this recently is still the hand's: feedback leaves it alone.
export const ECHO_MS = 250

const EMPTY = Object.freeze([])

// Frame-paced while the tab shows; a hidden tab gets no animation frames, and a show must not go deaf because the operator switched tabs.
const defaultSchedule = fn =>
	typeof document !== 'undefined' && !document.hidden && typeof requestAnimationFrame === 'function'
		? requestAnimationFrame(fn)
		: setTimeout(fn, 16)

export const createRouter = ({
	resolvePort,
	schedule = defaultSchedule,
	setTimer = setTimeout,
	clearTimer = clearTimeout,
	idleMs = IDLE_MS,
	now = () => performance.now(),
}) => {
	let index = new Map()
	// binding id → when its control last sent
	const heard = new Map()
	// One per binding target: `${id}#${i}`.
	const slots = new Map()
	const dirty = new Set()
	let queued = false

	const endGesture = slot => {
		if (slot.timer !== undefined) clearTimer(slot.timer)
		slot.timer = undefined
		const port = slot.port
		slot.port = null
		slot.value = undefined
		slot.acc = undefined
		port?.end?.()
	}

	const slotFor = (binding, i) => {
		const key = `${binding.id}#${i}`
		const t = binding.targets[i]
		let slot = slots.get(key)
		if (slot && slot.t.target === t.target) {
			slot.binding = binding
			slot.t = t
			return slot
		}
		if (slot) endGesture(slot)
		slot = {
			binding,
			t,
			spec: parseTarget(t.target),
			to: createTakeover(),
			btn: false,
			pending: null,
			port: null,
			value: undefined,
			acc: undefined,
			timer: undefined,
			// The schema last written through, kept past the gesture for `ghosts`.
			schema: null,
		}
		slots.set(key, slot)
		return slot
	}

	const queue = (slot, kind, value) => {
		const p = slot.pending ?? {}
		if (kind === 'rel') {
			p.rel = (p.rel ?? 0) + value
			delete p.abs
		} else {
			p.abs = value
			p.jump = kind === 'jump'
			delete p.rel
		}
		slot.pending = p
		dirty.add(slot)
		if (!queued) {
			queued = true
			schedule(flush)
		}
	}

	/** A one-shot write — a button, a velocity. Not a gesture: one write, one undo step. */
	const once = (slot, positionOf) => {
		if (slot.port) return
		const port = resolvePort(slot.spec)
		if (!port?.schema) return
		const v = toDomain(positionOf(fromDomain(port.read(), port.schema, slot.t)), port.schema, slot.t)
		if (port.set) port.set(v)
		else {
			port.begin?.()
			port.write(v)
			port.end?.()
		}
		noteWritten(slot.to, fromDomain(v, port.schema, slot.t))
	}

	function flush() {
		queued = false
		const batch = [...dirty]
		dirty.clear()
		for (const slot of batch) {
			const p = slot.pending
			slot.pending = null
			if (!p) continue
			const fresh = !slot.port
			const port = slot.port ?? resolvePort(slot.spec)
			if (!port?.schema) continue
			const { schema } = port
			slot.schema = schema
			const pos = fromDomain(fresh ? port.read() : slot.value, schema, slot.t)
			let hw
			if (p.rel !== undefined) {
				if (slot.acc === undefined) slot.acc = pos
				slot.acc = clamp01(slot.acc + p.rel * relativeTick(schema, slot.t))
				hw = slot.acc
			} else {
				const mode = p.jump ? 'jump' : (port.takeover ?? slot.binding.takeover)
				hw = takeover(mode, slot.to, p.abs, pos)
				if (hw === undefined) continue
			}
			const v = toDomain(hw, schema, slot.t)
			if (fresh) {
				slot.port = port
				port.begin?.()
			}
			if (fresh || v !== slot.value) port.write(v)
			slot.value = v
			noteWritten(slot.to, fromDomain(v, schema, slot.t))
			if (slot.timer !== undefined) clearTimer(slot.timer)
			slot.timer = setTimer(() => endGesture(slot), port.idleMs ?? idleMs)
		}
	}

	const dispatch = (binding, ev) => {
		if (!binding?.enabled || !ev) return
		heard.set(binding.id, now())
		binding.targets.forEach((_t, i) => {
			const slot = slotFor(binding, i)
			const { spec } = slot
			if (!spec) return
			let kind = ev.kind
			// A pad in the range loads its preset; a CC button counts once past the midpoint, as a press.
			if (spec.kind === 'presets') {
				if (kind === 'press' || (kind === 'abs' && ev.value >= 0.5))
					resolvePort(spec)?.fire(ev.number + (slot.t.offset ?? 0))
				return
			}
			if ((spec.kind === 'action' || BUTTON_MODES.has(binding.mode)) && kind === 'abs') {
				const on = ev.value >= 0.5
				if (on === slot.btn) return
				kind = on ? 'press' : 'release'
			}
			if (kind === 'press') slot.btn = true
			else if (kind === 'release') slot.btn = false

			if (spec.kind === 'action') {
				if (kind === 'press' || (kind === 'rel' && ev.value !== 0)) resolvePort(spec)?.fire()
				return
			}
			switch (binding.mode) {
				case 'absolute':
					if (kind === 'abs') queue(slot, 'abs', ev.value)
					else if (kind === 'press') queue(slot, 'abs', 1)
					return
				case 'relative':
					if (kind === 'rel' && ev.value) queue(slot, 'rel', ev.value)
					return
				case 'velocity':
					if (kind === 'press') once(slot, () => ev.value)
					else if (kind === 'abs') queue(slot, 'jump', ev.value)
					return
				case 'toggle':
					if (kind === 'press') once(slot, pos => (pos >= 0.5 ? 0 : 1))
					return
				case 'momentary':
					if (kind === 'press') once(slot, () => 1)
					else if (kind === 'release') once(slot, () => 0)
					return
				case 'trigger':
					if (kind === 'press') once(slot, () => 1)
			}
		})
	}

	const setBindings = bindings => {
		const next = new Map()
		const byId = new Map()
		const add = (key, b) => {
			const list = next.get(key)
			if (list) list.push(b)
			else next.set(key, [b])
		}
		const ranges = []
		for (const b of bindings ?? EMPTY) {
			if (!b?.enabled) continue
			const key = sourceKey(b.source)
			if (!key) continue
			byId.set(b.id, b)
			if (rangeEntry(b) && b.source.number !== undefined) ranges.push(b)
			else add(key, b)
		}
		// A range answers every number of its message type, except one another control's binding already has.
		for (const b of ranges) {
			for (let n = 0; n < RANGE_SIZE; n++) {
				const key = sourceKey({ ...b.source, number: n })
				if (!next.get(key)?.some(x => !rangeEntry(x))) add(key, b)
			}
		}
		index = next
		for (const id of heard.keys()) if (!byId.has(id)) heard.delete(id)
		for (const [key, slot] of slots) {
			const b = byId.get(slot.binding.id)
			if (b?.targets[Number(key.slice(key.lastIndexOf('#') + 1))]?.target === slot.t.target) continue
			endGesture(slot)
			slots.delete(key)
		}
	}

	const bindingsFor = key => index.get(key) ?? EMPTY

	/** A backend's event, routed to every binding on its source. */
	const handle = ev => {
		for (const b of bindingsFor(ev.sourceKey)) dispatch(b, ev)
	}

	/**
	 * Where each control waiting for pickup is: target string → the hardware position on the target's whole range (0…1), for Learn's ghost marker. Only absolute controls that have moved and not caught; a target bound twice shows the last slot.
	 */
	const ghosts = () => {
		const out = new Map()
		for (const slot of slots.values()) {
			const { to, schema, binding, t } = slot
			if (!schema || to.caught || to.prev === undefined || binding.mode !== 'absolute') continue
			out.set(t.target, fromDomain(toDomain(to.prev, schema, t), schema))
		}
		return out
	}

	/** Is a binding's control moving: heard within `ECHO_MS`, or its gesture still open. */
	const busy = id => {
		const t = heard.get(id)
		return (t !== undefined && now() - t < ECHO_MS) || !!slots.get(`${id}#0`)?.port
	}

	/** End every gesture now — the backend stopped, or the page is going. */
	const release = () => {
		flush()
		for (const slot of slots.values()) endGesture(slot)
	}

	return { setBindings, bindingsFor, dispatch, handle, flush, release, ghosts, busy }
}
